import React, { useEffect, useState } from 'react';
import { getSessionHeaders } from '../../lib/serverSession';
import { listAnnouncementMedia, saveAnnouncementMedia, type GalleryItem } from '../admin/announcementMedia';
import './media-gallery.css';

async function listPublicMedia(kind: 'image' | 'video') {
  const response = await fetch('/api/user/media-gallery', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
    body: JSON.stringify({ action: 'list', kind }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body?.success === false) throw new Error(body?.error || 'The gallery could not be opened.');
  return (Array.isArray(body.media) ? body.media : []) as GalleryItem[];
}

export const MediaGallery: React.FC<{
  mode?: 'manage' | 'showcase';
  onInsert?: (item: GalleryItem) => void;
  onError?: (message: string) => void;
}> = ({ mode = 'manage', onInsert, onError }) => {
  const [kind, setKind] = useState<'image' | 'video'>('image');
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const report = (message: string) => onError?.(message);

  useEffect(() => {
    const load = mode === 'showcase' ? listPublicMedia(kind) : listAnnouncementMedia();
    load.then(setItems).catch((err) => report(err instanceof Error ? err.message : 'The gallery could not be opened.'));
  }, [mode, kind]);

  const takeFiles = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (!list.length) return;
    setBusy(true);
    try {
      for (const file of list) {
        const saved = await saveAnnouncementMedia(file);
        setItems((current) => [saved, ...current]);
      }
    } catch (err) {
      report(err instanceof Error ? err.message : 'The file could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  const visible = mode === 'showcase' ? items : items;
  const slide = open === null ? null : visible[open] || null;

  useEffect(() => {
    if (open === null) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(null);
      if (event.key === 'ArrowRight') setOpen((current) => (current === null ? current : (current + 1) % visible.length));
      if (event.key === 'ArrowLeft') setOpen((current) => (current === null ? current : (current - 1 + visible.length) % visible.length));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, visible.length]);

  return (
    <section className={`aureus-gallery${mode === 'showcase' ? ' aureus-gallery--showcase' : ''}`} aria-label="Media gallery">
      <div className="aureus-gallery-head">
        <div>
          <h3>Media gallery</h3>
          <p>
            {mode === 'showcase'
              ? 'Photos and videos saved for the site. Open one to move through the set.'
              : 'Drop pictures or videos here. Drag one onto a page, or insert it. Pictures are saved small for the page and large for the slideshow.'}
          </p>
        </div>
        {mode === 'manage' ? (
          <label className="aureus-gallery-pick">
            Add files
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm"
              multiple
              onChange={(event) => {
                if (event.target.files) takeFiles(event.target.files);
                event.target.value = '';
              }}
            />
          </label>
        ) : null}
      </div>
      {mode === 'showcase' ? (
        <div className="aureus-gallery-tabs">
          <button type="button" className={kind === 'image' ? 'is-on' : ''} onClick={() => { setKind('image'); setOpen(null); }}>Photos</button>
          <button type="button" className={kind === 'video' ? 'is-on' : ''} onClick={() => { setKind('video'); setOpen(null); }}>Videos</button>
        </div>
      ) : (
        <div
          className={`aureus-gallery-drop${over ? ' is-over' : ''}`}
          onDragOver={(event) => { event.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(event) => {
            event.preventDefault();
            setOver(false);
            if (event.dataTransfer.files?.length) takeFiles(event.dataTransfer.files);
          }}
        >
          <p>{busy ? 'Saving…' : 'Drop files to upload'}</p>
        </div>
      )}
      <div className="aureus-gallery-grid">
        {visible.map((item, index) => {
          const card = (
            <>
              {item.kind === 'video' || item.videoUrl ? (
                <video src={item.videoUrl || undefined} poster={item.posterUrl || undefined} muted />
              ) : (
                <img src={item.imageUrl || undefined} alt="" />
              )}
              {onInsert ? <button type="button" onClick={() => onInsert(item)}>Insert</button> : null}
            </>
          );
          if (mode === 'showcase') {
            return (
              <button key={item.mediaId || index} type="button" className="aureus-gallery-card" onClick={() => setOpen(index)}>
                {card}
              </button>
            );
          }
          return (
            <article
              key={item.mediaId || item.imagePath || item.videoPath || index}
              className="aureus-gallery-card"
              draggable
              onDragStart={(event) => {
                event.dataTransfer.setData('application/x-aureus-media', JSON.stringify(item));
                event.dataTransfer.effectAllowed = 'copy';
              }}
            >
              {card}
            </article>
          );
        })}
      </div>
      {visible.length === 0 ? <p style={{ marginTop: 12 }}>Nothing in this gallery yet.</p> : null}
      {slide ? (
        <div className="aureus-gallery-show" role="dialog" aria-modal="true" aria-label="Gallery slideshow">
          <button type="button" onClick={() => setOpen((current) => (current === null ? current : (current - 1 + visible.length) % visible.length))} aria-label="Previous">‹</button>
          {slide.videoUrl ? (
            <video src={slide.videoUrl} poster={slide.posterUrl || undefined} controls autoPlay playsInline />
          ) : (
            <img src={slide.fullUrl || slide.imageUrl || ''} alt="" />
          )}
          <button type="button" onClick={() => setOpen((current) => (current === null ? current : (current + 1) % visible.length))} aria-label="Next">›</button>
          <button type="button" className="aureus-gallery-show-close" onClick={() => setOpen(null)} aria-label="Close">×</button>
        </div>
      ) : null}
    </section>
  );
};

export default MediaGallery;
